import { TextInput, Button, Flex, Card, NumberInput, Box } from '@applyboard/crystal-ui';
import { ArrowRightOutlineIcon, DeleteOutlineIcon } from '@applyboard/ui-icons';
import { RuleWithId, GradeSchemaInfo } from '~/types/gradeConversion';
import { ConversionTableRule } from '@ab/ams-school-api/src/services/content/v1/ConversionTables/types';

interface NumericDestinationRulesTableProps {
  rules: RuleWithId[];
  gradeSchema: GradeSchemaInfo;
  onUpdateRule: (index: number, updates: Partial<ConversionTableRule>) => void;
  onRemoveRule: (index: number) => void;
}

export function NumericDestinationRulesTable({
  rules,
  gradeSchema,
  onUpdateRule,
  onRemoveRule,
}: NumericDestinationRulesTableProps) {
  const isNumericSource = gradeSchema.gradeSchemaType === 'numeric';

  return (
    <Flex direction="column" gap={4}>
      {rules.map((rule, rowIndex) => (
        <Card key={rule.tempId ?? rowIndex} emphasis="outlined">
          <Flex direction="column" gap={3}>
            <Flex direction="row" gap={4} align="center" wrap>
              {isNumericSource ? (
                <>
                  <Flex.Item grow={1} basis="240px">
                    <NumberInput
                      onChange={(value) =>
                        onUpdateRule(rowIndex, {
                          sourceRangeLow: value ? parseFloat(value) : undefined,
                        })
                      }
                      value={rule.sourceRangeLow?.toString() ?? ''}
                      label="Source Range Low"
                      size="sm"
                    />
                  </Flex.Item>
                  <Flex.Item grow={1} basis="240px">
                    <NumberInput
                      onChange={(value) =>
                        onUpdateRule(rowIndex, {
                          sourceRangeHigh: value ? parseFloat(value) : undefined,
                        })
                      }
                      value={rule.sourceRangeHigh?.toString() ?? ''}
                      label="Source Range High"
                      size="sm"
                    />
                  </Flex.Item>
                </>
              ) : (
                <Flex.Item grow={1} basis="240px">
                  <TextInput
                    onChange={(value) =>
                      onUpdateRule(rowIndex, {
                        sourceLetterGrade: value,
                      })
                    }
                    value={rule.sourceLetterGrade ?? ''}
                    label="Source Letter Grade"
                    size="sm"
                    placeholder="e.g., A+"
                  />
                </Flex.Item>
              )}

              <Flex.Item align="center">
                <Box pt={8}>
                  <ArrowRightOutlineIcon />
                </Box>
              </Flex.Item>

              {/* Destination Grade Fields - Conditional based on source type */}
              {isNumericSource ? (
                <>
                  <Flex.Item grow={1} basis="240px">
                    <NumberInput
                      onChange={(value) =>
                        onUpdateRule(rowIndex, {
                          destinationRangeLow: value ? parseFloat(value) : undefined,
                        })
                      }
                      value={rule.destinationRangeLow?.toString() ?? ''}
                      label="Destination Grade Low"
                      size="sm"
                      placeholder="e.g., 70"
                    />
                  </Flex.Item>
                  <Flex.Item grow={1} basis="240px">
                    <NumberInput
                      onChange={(value) =>
                        onUpdateRule(rowIndex, {
                          destinationRangeHigh: value ? parseFloat(value) : undefined,
                        })
                      }
                      value={rule.destinationRangeHigh?.toString() ?? ''}
                      label="Destination Grade High"
                      size="sm"
                      placeholder="e.g., 79"
                    />
                  </Flex.Item>
                </>
              ) : (
                <Flex.Item grow={1} basis="240px">
                  <NumberInput
                    onChange={(value) =>
                      onUpdateRule(rowIndex, {
                        destinationSingleGrade: value ? parseFloat(value) : undefined,
                      })
                    }
                    value={rule.destinationSingleGrade?.toString() ?? ''}
                    label="Destination Grade"
                    size="sm"
                    placeholder="e.g., 79"
                  />
                </Flex.Item>
              )}

              <Button
                size="sm"
                onClick={() => onRemoveRule(rowIndex)}
                aria-label="Delete Rule"
                leadIcon={DeleteOutlineIcon}
                intent="negative"
                emphasis="transparent"
                disabled={rules.length === 1}
              />
            </Flex>
          </Flex>
        </Card>
      ))}
    </Flex>
  );
}
